import React, { Suspense } from 'react';
import { useLandingPageTest } from '../utils/abTesting';
import { useAnalytics, useScrollTracking, useTimeTracking } from '../utils/analytics';

/**
 * Smart Landing Page Component
 * Automatically serves the optimal landing page based on A/B testing
 */
const SmartLandingPage: React.FC = () => {
  const { Component, variation, isLoading } = useLandingPageTest();
  const analytics = useAnalytics();
  
  // Track user engagement
  useScrollTracking();
  useTimeTracking();

  // Track page view and A/B test exposure
  React.useEffect(() => {
    if (!isLoading && variation) {
      analytics.trackPageView('/', {
        abTestVariation: variation,
        timestamp: new Date().toISOString()
      });
      
      analytics.trackConversion({
        type: 'page_view',
        source: `ab_test_${variation}`,
        properties: {
          testId: 'landing_page_v1',
          variation,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        }
      });
    }
  }, [isLoading, variation, analytics]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading AutoReelr...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense 
      fallback={
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-white/20 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-white/10 rounded w-48 mx-auto mb-2"></div>
              <div className="h-4 bg-white/10 rounded w-56 mx-auto"></div>
            </div>
          </div>
        </div>
      }
    >
      {/* Add analytics context to component */}
      <div data-ab-test="landing_page_v1" data-variation={variation}>
        <Component />
      </div>
    </Suspense>
  );
};

export default SmartLandingPage;